import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToggle } from "@/hooks/useToggle";

import { Building, PlusCircle } from "lucide-react";

import Dashboard from "@/components/pages/dashboard";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AddOpportunityDialog } from "@/components/dialogs/company/opportunity/add-opportunity";
import { CompanyOpportunityRow } from "@/components/opportunity/company";
import CompanyScheduleInterviewSheet from "@/components/dialogs/company/schedule-interview";

import { useMyCompanyQuery } from "@/queries/company/my-company";
import { useCompanyOpportunitiesQuery } from "@/queries/company/opportunities";

import { CompanyJobOpening } from "@/types";
import { useConfirmCheckout } from "@/mutations/company/opening/confirm-checkout";

type Section = "active" | "closed";

const filters: { label: string; value: Section }[] = [
  { label: "Active", value: "active" },
  { label: "Contract", value: "closed" },
];

const isActive = ({ status }: CompanyJobOpening) =>
  status !== "Role Filled (won)" && status !== "Role Filled (lost)";

const isClosed = ({ status }: CompanyJobOpening) =>
  status === "Role Filled (won)" || status === "Role Filled (lost)";

const CompanyOpportunities = () => {
  const navigate = useNavigate();
  const { companyLoading, company } = useMyCompanyQuery();
  const { opportunities } = useCompanyOpportunitiesQuery();

  const { confirmCheckout } = useConfirmCheckout();

  const [oppDialogOpen, toggleOppDialog] = useToggle();

  const [section, setSection] = useState<Section>("active");
  const [focus, setFocus] = useState<string | null>(null);

  const params = new URLSearchParams(window.location.search);
  const paymentStatus = params.get("payment_status");
  const sessionId = params.get("session_id");
  const focusId = params.get("focus");

  useEffect(() => {
    if (!focusId) {
      return;
    }

    const opportunity = opportunities?.find(({ id }) => id === focusId);

    if (!opportunity) {
      return;
    }

    const section: Section = isClosed(opportunity) ? "closed" : "active";

    setSection(section);
    setFocus(focusId);

    const url = new URL(window.location.href);
    url.searchParams.delete("focus");
    window.history.replaceState({}, "", url.toString());
  }, [focusId, opportunities]);

  useEffect(() => {
    if (paymentStatus !== "success" || !sessionId) return;

    confirmCheckout({ sessionId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatus, sessionId]);

  const filteredOpportunities = useMemo(() => {
    if (!opportunities) {
      return [];
    } else if (section === "active") {
      return opportunities.filter(isActive);
    } else {
      return opportunities.filter(isClosed);
    }
  }, [opportunities, section]);

  return (
    <>
      <Dashboard.Root>
        <Dashboard.ProfileRow>
          <Dashboard.ProfileInfo
            title={company?.name}
            description={company?.location}
            fallbackIcon={Building}
            src={company?.logoLink}
            loading={companyLoading}
          />
          <Dashboard.ProfileActions>
            <Button
              variant="secondary"
              disabled={companyLoading}
              onClick={() => navigate("/company/profile/edit")}
            >
              <Building />
              Update Company Profile
            </Button>
            <AddOpportunityDialog
              open={oppDialogOpen}
              setOpen={toggleOppDialog}
            />
          </Dashboard.ProfileActions>
        </Dashboard.ProfileRow>
        <Dashboard.MainContent className="md:col-span-2 md:col-start-1">
          <ToggleGroup
            type="single"
            value={section}
            onValueChange={(value) => setSection(value as Section)}
            className="hidden md:inline-flex w-full"
          >
            {filters.map((filter) => (
              <ToggleGroupItem
                key={`toggle-${filter.value}`}
                value={filter.value}
              >
                {filter.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <div className="block md:hidden w-full">
            <Select
              value={section}
              onValueChange={(value) => setSection(value as Section)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                {filters.map((filter) => (
                  <SelectItem
                    key={`select-${filter.value}`}
                    value={filter.value}
                  >
                    {filter.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {opportunities && opportunities.length > 0 ? (
            <OpportunitiesSection
              opportunities={filteredOpportunities}
              focus={focus}
            />
          ) : (
            <div className="flex flex-col items-center gap-1">
              <p className="w-full text-sm text-muted-foreground text-center">
                No opportunities found. Create your first one!
              </p>
              <Button
                disabled={companyLoading}
                onClick={() => toggleOppDialog()}
              >
                <PlusCircle />
                Add An Opportunity
              </Button>
            </div>
          )}
        </Dashboard.MainContent>
      </Dashboard.Root>
      <CompanyScheduleInterviewSheet />
    </>
  );
};

export default CompanyOpportunities;

const OpportunitiesSection = ({
  opportunities,
  focus,
}: {
  opportunities: CompanyJobOpening[];
  focus: string | null;
}) => {
  return (
    <div className="w-full flex flex-col gap-4">
      <div className="w-full flex flex-col gap-4">
        {opportunities.length > 0 ? (
          opportunities.map((opportunity, index) => (
            <CompanyOpportunityRow
              key={opportunity.id}
              id={index}
              opportunity={opportunity}
              highlight={focus === opportunity.id}
            />
          ))
        ) : (
          <p className="w-full text-sm text-muted-foreground text-center">
            No opportunities found. Please check back later.
          </p>
        )}
      </div>
    </div>
  );
};
